// @ts-strict-ignore
import { ReactNode, SyntheticEvent, useEffect, useId, useState } from 'react';
import styled from 'styled-components';
import { useSlate, ReactEditor } from 'slate-react';
import { useSelector } from 'src/store/store';
import MarkButton from 'src/components/textEditor/components/buttons';
import { MyEditor } from 'src/components/textEditor/customHelpers';
import EmojiPickerContainer from 'src/components/textEditor/components/emojiPickerContainer';
import { Emoji } from 'src/constants/types';
import { hideImageUpload } from 'src/constants/restrictedCommunities';
type Props = {
  children?: ReactNode;
  className?: string;
  dataTestId?: string;
  hasImage?: boolean;
  hasMentions?: boolean;
  isActive?: boolean;
  isEmojiPickerClosed?: boolean;
  onAddImg?: () => void;
};

// navigation of toolbar buttons works with Tab for next button, and shift+tab for previous button. On enter, one can select
// if selection - selection disappears on 'Enter' of a toolbar button, BUT! stays in-editor, so any other button 'enter' hit works still
export default function EditorToolbar({
  children,
  className,
  dataTestId,
  hasImage = true,
  hasMentions = true,
  isActive = true,
  isEmojiPickerClosed = false,
  // to use for closing from outside
  onAddImg
}: Props) {
  const editorCurrent = useSlate();
  const [isShowEmojiPicker, setIsShowEmojiPicker] = useState(false);
  const isImageUploadVisible = useShowImageUpload(hasImage);
  const imageUploadId = useId().replaceAll(':', '');
  useEffect(() => {
    if (isEmojiPickerClosed) setIsShowEmojiPicker(false);
  }, [isEmojiPickerClosed]);
  const toggleMark = (format: string) => {
    const isActive = MyEditor.isMarkActive(editorCurrent, format);
    MyEditor.toggleMarkToolbar(editorCurrent, format, isActive);
  };
  const addMentionEntry = () => {
    if (hasMentions) {
      editorCurrent.insertText('@');
      MyEditor.focus(editorCurrent);
    }
  };
  const addImage = (e: SyntheticEvent) => {
    if (isImageUploadVisible) {
      const target = (e.target as HTMLInputElement);
      const currentFiles = target.files; // FileList object (similar to NodeList)
      if (!currentFiles || currentFiles && currentFiles.length === 0) return;
      MyEditor.insertImage({
        editor: editorCurrent,
        url: null,
        file: currentFiles[0]
      });
      if (onAddImg) onAddImg();
    }
  };
  const addEmoji = ({
    native
  }: Emoji) => {
    // add emojis as part of text; breaks from text on italic/underline/bold
    editorCurrent.insertNode({
      text: native
    });
    ReactEditor.focus(editorCurrent);
  };
  return <>
      <MenuContainer aria-label="Text editor toolbar" className={className} data-testid={dataTestId} isActive={isActive} data-sentry-element="MenuContainer" data-sentry-source-file="editorToolbar.tsx">
        <MarkButton aria="Add bold" dataTestId="bold-icon" format="bold" isActive={MyEditor.isMarkActive(editorCurrent, 'bold')} toggleMark={toggleMark} data-sentry-element="MarkButton" data-sentry-source-file="editorToolbar.tsx" />
        <MarkButton aria="Add italic" dataTestId="italic-icon" format="italic" isActive={MyEditor.isMarkActive(editorCurrent, 'italic')} toggleMark={toggleMark} data-sentry-element="MarkButton" data-sentry-source-file="editorToolbar.tsx" />
        <MarkButton aria="Add underline" dataTestId="underline-icon" format="underline" isActive={MyEditor.isMarkActive(editorCurrent, 'underline')} toggleMark={toggleMark} data-sentry-element="MarkButton" data-sentry-source-file="editorToolbar.tsx" />
        {hasMentions && <MarkButton aria="Add mention" dataTestId="mention-icon" format="at" isActive={false} toggleMark={addMentionEntry} />}
        <MarkButton aria="Add emoji" dataTestId="emoji-icon" format="smile" isActive={isShowEmojiPicker} toggleMark={() => setIsShowEmojiPicker(!isShowEmojiPicker)} data-sentry-element="MarkButton" data-sentry-source-file="editorToolbar.tsx" />
        {isImageUploadVisible && <>
            <UploadInput accept="image/*" id={imageUploadId + '-img-upload'} type="file" onChange={addImage} onClick={event => {
          // needs to clear out the target else same file selection won't register in onchange
          const element = (event.target as HTMLInputElement);
          element.value = '';
        }} />
            <MarkButton aria="Add image" format="picture" htmlFor={imageUploadId + '-img-upload'} isActive={false} toggleMark={() => {}} />
          </>}
        {children}
      </MenuContainer>
      {isShowEmojiPicker && <EmojiPickerContainer onSelect={addEmoji} />}
    </>;
}
const useShowImageUpload = (hasImage: boolean) => {
  const slug = useSelector(({
    context
  }) => context.currentCommunity?.slug);
  if (!slug) return false;
  if (hideImageUpload.includes(slug)) return false;
  return hasImage;
};
const MenuContainer = styled.div<{
  width?: string;
  isActive: boolean;
}>`
  display: ${props => props.isActive ? 'inline-block' : 'none'};
  vertical-align: bottom;
  width: 100%;
  border-top: ${({
  theme
}) => theme.greyLine};
  padding: 10px 0;
  margin-bottom: 5px; // margin - in safari, there's another 'padding' for outline, and bottom of outline would be hidden
  margin-top: 10px;
  & > * {
    display: inline-block;
  }
`;
const UploadInput = styled.input`
  border: 0;
  clip: rect(0, 0, 0, 0);
  height: 1px;
  overflow: hidden;
  padding: 0;
  position: absolute !important;
  white-space: nowrap;
  width: 1px;
`;